import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Tooltip } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import AppShell from '../../components/layout/AppShell';
import PageHeader from '../../components/layout/PageHeader';
import KpiCard from '../../components/ui/KpiCard';
import EmptyState from '../../components/ui/EmptyState';
import { BellIcon, TableRestaurant, Hourglass, CheckCircle } from '../../components/ui/Icons';
import useServiceRequestsData from '../../hooks/useServiceRequestsData';
import ServiceRequestCard from '../../components/Manager/ServiceRequestCard';
import { getAgeMinutes } from '../../components/Manager/serviceRequestHelpers';
import '../../components/Manager/ServiceRequestsBoard.css';

/**
 * Standalone Service Requests page for managers. Unlike the compact
 * ServiceRequestsPanel embedded in the dashboard, this is a full-width board:
 * summary KPIs, status/priority filters, a sort control, and a responsive
 * card grid with escalating wait-time emphasis. Shares its realtime data
 * layer with the dashboard widget via useServiceRequestsData.
 */

const STATUS_FILTERS = ['all', 'pending', 'acknowledged'];
const PRIORITY_FILTERS = ['all', 'high', 'medium', 'low'];

const isHighPriority = (p) => p === 'high' || p === 'urgent';
const priorityRank = (p) => (isHighPriority(p) ? 0 : p === 'medium' ? 1 : 2);

const matchesPriority = (request, filter) => {
  if (filter === 'all') return true;
  if (filter === 'high') return isHighPriority(request.priority);
  return request.priority === filter;
};

const formatCompactWait = (minutes) => {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
};

const ServiceRequests = () => {
  const { t } = useTranslation();
  const { restaurants } = useAuth();
  const restaurant = restaurants?.[0];

  const { requests, loading, soundEnabled, setSoundEnabled, acknowledge, complete } =
    useServiceRequestsData(restaurant?.id);

  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sortBy, setSortBy] = useState('oldest');

  // ── Derived stats ─────────────────────────────────────────────
  const stats = useMemo(() => {
    const pending = requests.filter((r) => r.status === 'pending').length;
    const urgent = requests.filter((r) => isHighPriority(r.priority)).length;
    const oldest = requests.reduce(
      (max, r) => Math.max(max, getAgeMinutes(r.created_at)),
      0
    );
    return {
      active: requests.length,
      pending,
      urgent,
      longestWait: requests.length ? formatCompactWait(oldest) : '—',
    };
  }, [requests]);

  // Counts per status filter (shown on the chips)
  const statusCounts = useMemo(
    () => ({
      all: requests.length,
      pending: requests.filter((r) => r.status === 'pending').length,
      acknowledged: requests.filter((r) => r.status === 'acknowledged').length,
    }),
    [requests]
  );

  // ── Filtered + sorted list ────────────────────────────────────
  const visibleRequests = useMemo(() => {
    const filtered = requests.filter(
      (r) =>
        (statusFilter === 'all' || r.status === statusFilter) &&
        matchesPriority(r, priorityFilter)
    );
    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'priority') {
        const rank = priorityRank(a.priority) - priorityRank(b.priority);
        if (rank !== 0) return rank;
      }
      // oldest first (also the tiebreaker for priority sort)
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    });
    return sorted;
  }, [requests, statusFilter, priorityFilter, sortBy]);

  const filtersActive = statusFilter !== 'all' || priorityFilter !== 'all';
  const clearFilters = () => {
    setStatusFilter('all');
    setPriorityFilter('all');
  };

  // ── Sound toggle (header action) ──────────────────────────────
  const soundLabel = soundEnabled
    ? t('serviceRequests.muteSound', { defaultValue: 'Mute notifications' })
    : t('serviceRequests.enableSound', { defaultValue: 'Enable sound' });

  const soundAction = (
    <Tooltip title={soundLabel}>
      <IconButton
        onClick={() => setSoundEnabled(!soundEnabled)}
        aria-label={soundLabel}
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          border: '1px solid',
          borderColor: soundEnabled ? 'primary.main' : 'divider',
          backgroundColor: soundEnabled ? 'var(--primary-50)' : 'background.paper',
          color: soundEnabled ? 'primary.main' : 'text.disabled',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <BellIcon size={20} />
      </IconButton>
    </Tooltip>
  );

  // ── Toolbar (filters + sort) ──────────────────────────────────
  const toolbar = (
    <div className="sr-toolbar">
      <div
        className="sr-filter-group"
        role="group"
        aria-label={t('serviceRequests.filterByStatus', { defaultValue: 'Filter by status' })}
      >
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            type="button"
            className={`sr-chip ${statusFilter === s ? 'active' : ''}`}
            onClick={() => setStatusFilter(s)}
          >
            {t(`serviceRequests.status.${s}`, {
              defaultValue: s.charAt(0).toUpperCase() + s.slice(1),
            })}
            <span className="sr-chip-count">{statusCounts[s]}</span>
          </button>
        ))}
      </div>

      <div
        className="sr-filter-group"
        role="group"
        aria-label={t('serviceRequests.filterByPriority', { defaultValue: 'Filter by priority' })}
      >
        <span className="sr-filter-label">
          {t('serviceRequests.priorityLabel', { defaultValue: 'Priority' })}
        </span>
        {PRIORITY_FILTERS.map((p) => (
          <button
            key={p}
            type="button"
            className={`sr-chip ${priorityFilter === p ? 'active' : ''}`}
            onClick={() => setPriorityFilter(p)}
          >
            {t(`serviceRequests.priorityLevel.${p}`, {
              defaultValue: p.charAt(0).toUpperCase() + p.slice(1),
            })}
          </button>
        ))}
      </div>

      <div className="sr-sort">
        <span className="sr-filter-label">
          {t('serviceRequests.sortBy', { defaultValue: 'Sort' })}
        </span>
        <select
          className="sr-sort-select"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          aria-label={t('serviceRequests.sortBy', { defaultValue: 'Sort' })}
        >
          <option value="oldest">
            {t('serviceRequests.sort.oldest', { defaultValue: 'Longest waiting' })}
          </option>
          <option value="priority">
            {t('serviceRequests.sort.priority', { defaultValue: 'Priority' })}
          </option>
        </select>
      </div>
    </div>
  );

  if (!restaurant) {
    return (
      <AppShell maxWidth={false}>
        <PageHeader
          title={t('serviceRequests.title', 'Service Requests')}
          subtitle={t('serviceRequests.subtitle', 'Live table requests from your customers')}
        />
        <Box
          sx={{
            borderRadius: 2,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <EmptyState
            icon={<TableRestaurant size={32} />}
            title={t('serviceRequests.noRestaurantTitle', { defaultValue: 'No restaurant yet' })}
            description={t('serviceRequests.noRestaurant', {
              defaultValue: 'Create a restaurant to start receiving service requests.',
            })}
          />
        </Box>
      </AppShell>
    );
  }

  return (
    <AppShell maxWidth={false}>
      <PageHeader
        title={t('serviceRequests.title', 'Service Requests')}
        subtitle={t('serviceRequests.subtitle', 'Live table requests from your customers')}
        action={soundAction}
        toolbar={toolbar}
      />

      {/* ── Summary stats ──────────────────────────────────────── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        <KpiCard
          icon={<TableRestaurant size={22} />}
          iconTone="primary"
          label={t('serviceRequests.stat.active', { defaultValue: 'Active requests' })}
          value={stats.active}
        />
        <KpiCard
          icon={<Hourglass size={22} />}
          iconTone="secondary"
          label={t('serviceRequests.stat.awaiting', { defaultValue: 'Awaiting acknowledge' })}
          value={stats.pending}
        />
        <KpiCard
          icon={<BellIcon size={22} />}
          iconTone="danger"
          label={t('serviceRequests.stat.urgent', { defaultValue: 'Urgent' })}
          value={stats.urgent}
        />
        <KpiCard
          icon={<Hourglass size={22} />}
          iconTone="info"
          label={t('serviceRequests.stat.longestWait', { defaultValue: 'Longest wait' })}
          value={stats.longestWait}
        />
      </Box>

      {/* ── List / states ──────────────────────────────────────── */}
      {loading ? (
        <div className="sr-loading">
          <div className="sr-spinner" />
          <span>{t('common.loading', { defaultValue: 'Loading...' })}</span>
        </div>
      ) : requests.length === 0 ? (
        <EmptyState
          icon={<CheckCircle size={32} />}
          title={t('serviceRequests.allCaughtTitle', { defaultValue: 'All caught up' })}
          description={t('serviceRequests.allCaughtDesc', {
            defaultValue:
              'No tables are waiting right now. New requests appear here in real time.',
          })}
        />
      ) : visibleRequests.length === 0 ? (
        <EmptyState
          icon={<Hourglass size={32} />}
          title={t('serviceRequests.noMatchTitle', {
            defaultValue: 'No requests match this filter',
          })}
          description={t('serviceRequests.noMatchDesc', {
            defaultValue: 'Try a different status or priority to see more.',
          })}
          action={
            filtersActive ? (
              <button type="button" className="sr-chip active" onClick={clearFilters}>
                {t('serviceRequests.clearFilters', { defaultValue: 'Clear filters' })}
              </button>
            ) : null
          }
        />
      ) : (
        <div className="sr-grid">
          {visibleRequests.map((request) => (
            <ServiceRequestCard
              key={request.id}
              request={request}
              onAcknowledge={acknowledge}
              onComplete={complete}
            />
          ))}
        </div>
      )}
    </AppShell>
  );
};

export default ServiceRequests;
